import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonFooterBar,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface
} from 'src/components/widgets/skeleton';

const AreaBackgroundSkeletonShell = styled(SkeletonPageShell).attrs({ $padding: '20px 0 92px' })``;

const AreaBackgroundSkeletonHeader = styled(SkeletonPageHeader)``;

const AreaBackgroundTabsSkeleton = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 18px;
    min-width: 0;
`;

const AreaBackgroundTableSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    overflow: hidden;
    min-width: 0;
`;

const AreaBackgroundTableRowSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(180px, 1fr) 180px;
    gap: 18px;
    align-items: center;
    min-height: 58px;
    padding: 14px 18px;
    border-bottom: 1px solid ${(props) => props.theme.skeleton.backgroundColor};
    min-width: 0;

    &:last-child {
        border-bottom: 0;
    }

    @media (max-width: 700px) {
        grid-template-columns: minmax(0, 1fr) 72px;
    }
`;

const AreaBackgroundSectionSkeleton = styled(SkeletonSurface).attrs({ $padding: '24px' })``;

const AreaBackgroundCheckboxRowSkeleton = styled(SkeletonRow).attrs({ $gap: '12px' })`
    align-items: flex-start;
`;

const ColorSwatchesSkeleton = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-left: 30px;
`;

const AreaBackgroundFooterSkeleton = styled(SkeletonFooterBar)``;

export const FillBackgroundPageSkeleton: FC = () => (
    <AreaBackgroundSkeletonShell aria-hidden='true'>
        <AreaBackgroundSkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(240px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
            </SkeletonStack>
            <SkeletonBlock $width='92px' $height='36px' $radius='6px' />
        </AreaBackgroundSkeletonHeader>
        <AreaBackgroundTabsSkeleton>
            <SkeletonBlock $width='104px' $height='34px' $radius='17px' />
            <SkeletonBlock $width='150px' $height='34px' $radius='17px' />
        </AreaBackgroundTabsSkeleton>
        <AreaBackgroundTableSkeleton>
            {Array.from({ length: 7 }).map((_, index) => (
                <AreaBackgroundTableRowSkeleton key={index}>
                    <SkeletonBlock $width={index % 2 === 0 ? '240px' : '170px'} $height='15px' />
                    <SkeletonRow $gap='8px' $justify='flex-end'>
                        <SkeletonBlock $width='84px' $height='22px' $radius='11px' />
                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                    </SkeletonRow>
                </AreaBackgroundTableRowSkeleton>
            ))}
        </AreaBackgroundTableSkeleton>
    </AreaBackgroundSkeletonShell>
);

export const FillBackgroundRulePageSkeleton: FC = () => (
    <AreaBackgroundSkeletonShell aria-hidden='true'>
        <AreaBackgroundSkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(260px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(420px, 100%)' $height='13px' />
            </SkeletonStack>
        </AreaBackgroundSkeletonHeader>
        <SkeletonStack $gap='18px'>
            <AreaBackgroundSectionSkeleton>
                <SkeletonStack $gap='14px'>
                    <SkeletonBlock $width='86px' $height='20px' />
                    <SkeletonBlock $width='78px' $height='13px' />
                    <SkeletonBlock $height='38px' $radius='6px' />
                </SkeletonStack>
            </AreaBackgroundSectionSkeleton>
            <AreaBackgroundSectionSkeleton>
                <SkeletonStack $gap='18px'>
                    <SkeletonBlock $width='138px' $height='20px' />
                    <AreaBackgroundCheckboxRowSkeleton>
                        <SkeletonBlock $width='18px' $height='18px' $radius='3px' />
                        <SkeletonStack $gap='6px' $flex='1'>
                            <SkeletonBlock $width='112px' $height='15px' />
                            <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
                        </SkeletonStack>
                    </AreaBackgroundCheckboxRowSkeleton>
                    <ColorSwatchesSkeleton>
                        {Array.from({ length: 8 }).map((_, index) => (
                            <SkeletonBlock key={index} $width='28px' $height='28px' $round />
                        ))}
                    </ColorSwatchesSkeleton>
                    <SkeletonBlock $height='58px' $radius='8px' />
                    <AreaBackgroundCheckboxRowSkeleton>
                        <SkeletonBlock $width='18px' $height='18px' $radius='3px' />
                        <SkeletonStack $gap='6px' $flex='1'>
                            <SkeletonBlock $width='112px' $height='15px' />
                            <SkeletonBlock $width='min(560px, 100%)' $height='13px' />
                        </SkeletonStack>
                    </AreaBackgroundCheckboxRowSkeleton>
                    <AreaBackgroundCheckboxRowSkeleton>
                        <SkeletonBlock $width='18px' $height='18px' $radius='3px' />
                        <SkeletonStack $gap='6px' $flex='1'>
                            <SkeletonBlock $width='136px' $height='15px' />
                            <SkeletonBlock $width='min(620px, 100%)' $height='13px' />
                        </SkeletonStack>
                    </AreaBackgroundCheckboxRowSkeleton>
                </SkeletonStack>
            </AreaBackgroundSectionSkeleton>
        </SkeletonStack>
        <AreaBackgroundFooterSkeleton>
            <SkeletonBlock $width='110px' $height='36px' $radius='6px' />
            <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
        </AreaBackgroundFooterSkeleton>
    </AreaBackgroundSkeletonShell>
);
